import { createApi } from "@reduxjs/toolkit/query/react";

import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { API_DASHBOARD_LAYOUT } from "@/constants/apiEndpoints";
import type {
  ISaveDashboardLayoutRequest,
  TDashboardLayoutResponse,
  TDashboardScope,
} from "@/types/(company)/dashboardLayout.interface";

/**
 * The dashboard canvas is edited locally and flushed on a debounce, so a refetch
 * on every mount would race the pending write and briefly show stale widgets.
 * `refetchOnMountOrArgChange` is deliberately left off here — the tag
 * invalidation on save/reset is the only thing that re-reads the layout.
 */
export const dashboardLayoutApi = createApi({
  reducerPath: "dashboardLayoutApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["dashboardLayout"],
  endpoints: (builder) => ({
    getDashboardLayout: builder.query<
      TDashboardLayoutResponse,
      { scope: TDashboardScope }
    >({
      query: ({ scope }) => ({
        url: API_DASHBOARD_LAYOUT,
        method: "GET",
        params: { scope },
      }),
      providesTags: (_result, _error, { scope }) => [
        { type: "dashboardLayout", id: scope },
      ],
    }),

    /** Full replace — send every widget, not a patch. */
    saveDashboardLayout: builder.mutation<
      TDashboardLayoutResponse,
      ISaveDashboardLayoutRequest
    >({
      query: (body) => ({
        url: API_DASHBOARD_LAYOUT,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { scope }) => [
        { type: "dashboardLayout", id: scope },
      ],
    }),

    /** Drops the saved arrangement so the registry default takes over. */
    resetDashboardLayout: builder.mutation<
      TDashboardLayoutResponse,
      { scope: TDashboardScope }
    >({
      query: ({ scope }) => ({
        url: API_DASHBOARD_LAYOUT,
        method: "DELETE",
        params: { scope },
      }),
      invalidatesTags: (_result, _error, { scope }) => [
        { type: "dashboardLayout", id: scope },
      ],
    }),
  }),
});

export const {
  useGetDashboardLayoutQuery,
  useSaveDashboardLayoutMutation,
  useResetDashboardLayoutMutation,
} = dashboardLayoutApi;
